import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import axiosInstance from '../../_helpers/axios_instance';

interface SurveyQuestion {
  id: number;
  question: unknown;
  display_order: number;
  [key: string]: unknown;
}

interface SurveysState {
  survey_questions: SurveyQuestion[];
  id: number;
  fetchingSurvey: boolean;
  surveyFetchError: string | string[] | null;
}

const initialState: SurveysState = {
  survey_questions: [],
  id: 0,
  fetchingSurvey: false,
  surveyFetchError: null,
};

export const getSurvey = createAsyncThunk<unknown, any, { state: RootState }>(
  'survey/getSurvey',
  async ({ survey_id = process.env.REACT_APP_BURNOUT_SURVEY } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/surveys/${survey_id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  },
  {
    condition: (_, { getState }) => !getState().survey.fetchingSurvey,
  }
);

const surveySlice = createSlice({
  name: 'survey',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getSurvey.pending, (state) => {
        state.fetchingSurvey = true;
        state.surveyFetchError = null;
      })
      .addCase(getSurvey.fulfilled, (state, action) => {
        state.fetchingSurvey = false;
        Object.assign(state, action.payload);
        state.surveyFetchError = null;
      })
      .addCase(getSurvey.rejected, (state, action) => {
        state.fetchingSurvey = false;
        state.surveyFetchError = (action.payload as string | string[]) ?? null;
      });
  },
});

export default surveySlice.reducer;

const questionSort = (q1: any, q2: any) => q1.display_order - q2.display_order;

export const getAllQuestions = (surveyState: SurveysState) =>
  surveyState.survey_questions.map((q: any) => {
    const { id, question, type, instructions, short_question, question_num, category, choices, display_options, question_group } = q.question;
    return { id, question, type, instructions, short_question, question_num, category, choices, display_options, display_order: q.display_order, question_group };
  }).sort(questionSort);

export const getMultipleChoiceQuestionsForCharts = (surveyState: SurveysState) =>
  getAllQuestions(surveyState).filter((q: any) => q.type === 'MultipleChoiceQuestion' && q.display_options?.show_on_chart === true);

export const getChoices = (surveyState: SurveysState, question: any): unknown[] =>
  question?.choices ?? [];

export function getBurnoutQuestion(surveyState: SurveysState): unknown {
  return getAllQuestions(surveyState).find((q: any) => q.short_question === process.env.REACT_APP_BURNOUT_SHORT_QUESTION);
}

export function getAgreeDisagreeQuestions(surveyState: SurveysState): unknown[] {
  return getMultipleChoiceQuestionsForCharts(surveyState).filter(
    (q: any) => q.type === 'MultipleChoiceQuestion' && q.short_question !== process.env.REACT_APP_BURNOUT_SHORT_QUESTION
  );
}

export function getFreeTextQuestionsForCharts(surveyState: SurveysState): unknown[] {
  return getAllQuestions(surveyState).filter((q: any) => q.type === 'FreeTextQuestion' && q.display_options?.show_on_chart === true);
}

export function getDemographicQuestions(surveyState: SurveysState): unknown[] {
  return getAllQuestions(surveyState).filter(
    (q: any) =>
      q.category?.id === parseInt(process.env.REACT_APP_DEMOGRAPHIC_QUESTION_CATEGORY_ID as string) ||
      q.category?.name === 'Demographic'
  );
}
